'use client';

import * as React from 'react';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';

import { cn } from '../../lib/utils';

/**
 * Round checkbox, after glp-frontend's current `components/helpers/checkbox`:
 * the filled indicator covers the ring (`absolute -inset-px`) so the tick is
 * centred on it, and `checked="indeterminate"` draws a dash instead of a tick.
 *
 * Colours differ from glp on purpose: glp's `--yellow-grey-color` ring and
 * fill (#c8c8c8) is 1.7:1 on white; here the ring is gray-500 and the fill is
 * the brand green, both above the 3:1 non-text contrast minimum. Size it with
 * `h-*`/`w-*` on `className`; the indicator follows.
 */
const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>
>(({ className, checked, ...props }, ref) => (
  <CheckboxPrimitive.Root
    ref={ref}
    checked={checked}
    className={cn(
      'peer relative box-border h-5 w-5 shrink-0 rounded-full border border-gray-500 bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-global-green/40 focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:border-global-green data-[state=indeterminate]:border-global-green',
      className,
    )}
    {...props}
  >
    <CheckboxPrimitive.Indicator className='absolute -inset-px flex items-center justify-center text-global-green'>
      <svg viewBox='0 0 20 20' className='h-full w-full' aria-hidden='true'>
        <circle cx='10' cy='10' r='10' fill='currentColor' />
        {checked === 'indeterminate' ? (
          <path d='M5.5 10h9' stroke='white' strokeWidth='2.2' strokeLinecap='round' />
        ) : (
          <path
            d='M5.6 10.3l3 3 5.8-6.2'
            fill='none'
            stroke='white'
            strokeWidth='2.2'
            strokeLinecap='round'
            strokeLinejoin='round'
          />
        )}
      </svg>
    </CheckboxPrimitive.Indicator>
  </CheckboxPrimitive.Root>
));
Checkbox.displayName = CheckboxPrimitive.Root.displayName;

export { Checkbox };
